import React, { useEffect, useState } from 'react';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  Download, Send, RefreshCw, FileText, Loader2, CalendarClock, AlertCircle, CheckCircle,
} from 'lucide-react';

const ROLE_META = {
  member:    { label: 'Member',    hint: 'Individual members with an active or past subscription.' },
  merchant:  { label: 'Merchant',  hint: 'Approved merchant partners.' },
  mro:       { label: 'MRO',       hint: 'Merchant Relations Officers.' },
  moderator: { label: 'Moderator', hint: 'Content and support moderators.' },
  cro:       { label: 'CRO',       hint: 'Compliance & Reporting Officers.' },
  admin:     { label: 'Admin',     hint: 'Platform administrators.' },
  master:    { label: 'Master',    hint: 'All-in-one handbook — for internal leadership.' },
};

function fmtBytes(n) {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
}

export default function AdminTrainingPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(false);
  const [dialogRole, setDialogRole] = useState(null);
  const [downloading, setDownloading] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/training/manuals');
      setRows(data.manuals || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load manuals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    setRegen(true);
    try {
      const { data } = await api.post('/api/admin/training/regenerate');
      setRows(data.manuals || []);
      toast.success('All training manuals regenerated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Regeneration failed');
    } finally {
      setRegen(false);
    }
  };

  // Fetch the PDF as a blob and trigger a native save. Avoids the mobile
  // in-browser PDF viewer opening in the same tab with no back button.
  const downloadPdf = async (role, filename) => {
    setDownloading((d) => ({ ...d, [role]: true }));
    try {
      const res = await api.get(`/api/uploads/manuals/${filename}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading((d) => ({ ...d, [role]: false }));
    }
  };

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white flex items-center gap-2">
              <FileText className="w-7 h-7 text-[#00E5FF]" />
              Training Manuals
            </h1>
            <p className="text-[#A0B4CC] mt-1 text-sm">
              Regenerate role-specific PDF handbooks and distribute them to team leads or role cohorts.
            </p>
          </div>
          <Button
            onClick={regenerate}
            disabled={regen}
            className="btn-primary"
            data-testid="regenerate-manuals-btn"
          >
            {regen ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Regenerate All PDFs
          </Button>
        </div>

        {/* Info callout */}
        <div className="mb-6 p-4 rounded-xl bg-[#00E5FF]/5 border border-[#00E5FF]/20 text-[#A0B4CC] text-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-white mb-1">How this works</div>
              <ul className="list-disc list-inside space-y-1">
                <li>Content lives in <code className="text-[#00E5FF]">backend/services/training_manual.py</code>.</li>
                <li>After changing that file, hit <b>Regenerate All PDFs</b> — files are re-rendered to disk.</li>
                <li><b>Distribute</b> emails the PDF to Team Leads (admin + MRO), a full role cohort, or a custom list.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Manuals table */}
        <Card className="bg-[#041A3B] border-white/10 overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#000B18]/50 text-[#5C7599] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3">Role</th>
                    <th className="text-left px-4 py-3">Title</th>
                    <th className="text-left px-4 py-3">Size</th>
                    <th className="text-left px-4 py-3">Last Generated</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {loading && (
                    <tr><td colSpan={5} className="p-6 text-center text-[#5C7599]">Loading manuals…</td></tr>
                  )}
                  {!loading && rows.map((r) => (
                    <tr key={r.role} className="hover:bg-white/[0.02]" data-testid={`manual-row-${r.role}`}>
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs uppercase tracking-widest text-[#00E5FF]">{r.role}</div>
                        <div className="text-[10px] text-[#5C7599] mt-0.5">{ROLE_META[r.role]?.hint}</div>
                      </td>
                      <td className="px-4 py-3 text-white">{r.title}</td>
                      <td className="px-4 py-3 text-[#A0B4CC]">
                        {r.exists ? fmtBytes(r.size_bytes) : (
                          <span className="text-[#FFB800] text-xs inline-flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> Not yet generated
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#A0B4CC]">
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <CalendarClock className="w-3.5 h-3.5 text-[#5C7599]" />
                          {fmtDate(r.generated_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          <button
                            onClick={() => downloadPdf(r.role, r.filename)}
                            disabled={!r.exists || downloading[r.role]}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid={`download-${r.role}`}
                          >
                            {downloading[r.role]
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Download className="w-3.5 h-3.5" />}
                            Download
                          </button>
                          <Button
                            size="sm"
                            className="btn-primary text-xs"
                            onClick={() => setDialogRole(r.role)}
                            data-testid={`distribute-${r.role}`}
                          >
                            <Send className="w-3.5 h-3.5 mr-1" /> Distribute
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <DistributeDialog role={dialogRole} onClose={() => setDialogRole(null)} />
    </AdminSidebarLayout>
  );
}

function DistributeDialog({ role, onClose }) {
  const { api } = useAuth();
  const [targetKind, setTargetKind] = useState('team_leads');
  const [emails, setEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const open = !!role;

  useEffect(() => {
    if (!open) return;
    setTargetKind(role === 'master' ? 'team_leads' : 'role');
    setEmails('');
    setSubject('');
    setMessage('');
    setResult(null);
  }, [open, role]);

  const doSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const payload = { role, target_kind: targetKind };
      if (subject) payload.subject = subject;
      if (message) payload.message = message;
      if (targetKind === 'emails') {
        const list = emails.split(/[,\n\s;]+/).map((s) => s.trim()).filter(Boolean);
        if (list.length === 0) { toast.error('Please add at least one email'); setSending(false); return; }
        payload.emails = list;
      }
      const { data } = await api.post('/api/admin/training/send', payload);
      setResult(data);
      toast.success(`Sent to ${data.sent} of ${data.attempted} recipients`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#041A3B] border-white/10 text-white keep-dark max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[#00E5FF]" />
            Distribute {role?.toUpperCase()} Manual
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-[#A0B4CC] text-xs">Recipient group</Label>
            <Select value={targetKind} onValueChange={setTargetKind}>
              <SelectTrigger className="bg-[#000B18] border-white/10 text-white mt-1" data-testid="distribute-target">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#041A3B] border-white/10 text-white">
                <SelectItem value="team_leads">Team Leads (Admins{role !== 'master' ? ' + MROs' : ''})</SelectItem>
                {role !== 'master' && (
                  <SelectItem value="role">Everyone with role: {ROLE_META[role]?.label}</SelectItem>
                )}
                <SelectItem value="emails">Custom email list</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#5C7599] mt-1">
              {targetKind === 'team_leads' && (role === 'master' ? 'Sends to all admins.' : 'Sends to all admins + MROs.')}
              {targetKind === 'role' && `Sends to every ${ROLE_META[role]?.label} on the platform.`}
              {targetKind === 'emails' && 'Sends to the addresses you list below.'}
            </p>
          </div>

          {targetKind === 'emails' && (
            <div>
              <Label className="text-[#A0B4CC] text-xs">Email addresses (comma or newline separated)</Label>
              <Textarea
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                rows={4}
                placeholder="alice@example.com&#10;bob@example.com"
                className="bg-[#000B18] border-white/10 text-white mt-1 font-mono text-xs"
                data-testid="distribute-emails"
              />
            </div>
          )}

          <div>
            <Label className="text-[#A0B4CC] text-xs">Subject (optional)</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Uses the manual title if empty"
              className="bg-[#000B18] border-white/10 text-white mt-1"
              data-testid="distribute-subject"
            />
          </div>

          <div>
            <Label className="text-[#A0B4CC] text-xs">Message (optional)</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Optional cover message. Basic HTML supported."
              className="bg-[#000B18] border-white/10 text-white mt-1 text-sm"
              data-testid="distribute-message"
            />
          </div>

          {result && (
            <div className="rounded-lg bg-[#00E676]/10 border border-[#00E676]/30 p-3 text-sm">
              <div className="flex items-center gap-2 text-[#00E676] font-semibold">
                <CheckCircle className="w-4 h-4" /> Delivery report
              </div>
              <div className="text-[#A0B4CC] mt-1 text-xs">
                Attempted: {result.attempted} · Sent: {result.sent} · Failed: {result.failed}
              </div>
              {result.failed > 0 && (
                <details className="mt-2 text-xs text-[#FFB800]">
                  <summary className="cursor-pointer">Show failed addresses</summary>
                  <pre className="mt-1 whitespace-pre-wrap">{(result.failed_addresses || []).join('\n')}</pre>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="border-white/20 text-white hover:bg-white/5">
            Close
          </Button>
          <Button onClick={doSend} disabled={sending} className="btn-primary" data-testid="distribute-send">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
